// data/zones/zone2.ts — THE DROWNED ROOTS.
// DATA ONLY. Numbers, tables, ASCII maps. The behaviour is content/zones/zone2.ts
// (the flood, the water slow, the three brains) and content/bosses/boss2.ts.
//
// DESIGN §5 Zone 2: "the floor is a rhythm — water slows you (not them), and the
// flood comes on a timer you must read."
//
// The anti-reskin contract for this zone is the surge clock. Zone 1 asks where
// you stand relative to the *sources*; zone 2 asks where you stand relative to
// the *floor*, on a 25-second beat you can learn. Nothing here is zone 1 with a
// blue palette: 40-60% of every room is slow ground that periodically becomes
// lethal ground, the roster is built to hold you in it, and the boss is a
// terrain-control fight with no add-priority puzzle in it at all.

import type { EnemyDef, GeneratorDef, RoomTemplate, ZoneConfig, ZonePalette, Vec } from '../../types'

// ---------------------------------------------------------------- palette
// Flooded cistern-caverns: black water, wet blue-green cavern rock, and the
// bioluminescent root-light that is this zone's only source of colour. Torches
// are 0 on purpose — nothing burns down here (ZonePalette.torches, D-016).

export const ZONE2_PALETTE: ZonePalette = {
  // Dry ground is deliberately several stops lighter than the water: at a glance,
  // across a whole room, "can I stand here" has to be answerable without reading
  // a single tile edge.
  floor: '#263a42',
  floorAlt: '#2e474f',
  wall: '#3d626e',
  wallEdge: '#79a9b6',
  accent: '#2fd6a4',
  glow: '#5cf2cb',
  bg: '#040a0d',
  torches: 0,
}

/** Zone-2 specific colours the mechanic and the boss paint with. */
export const ZONE2_COLORS = {
  waterDeep: '#050f15',
  waterShallow: '#091d25',
  waterEdge: '#2e7b8a',
  caustic: '#6ff2d8',
  stone: '#3a5a65',
  stoneTop: '#7ba5b2',
  stoneShadow: '#050f14',
  root: '#3ee2b4',
  rootDim: '#166b58',
  surge: '#7ff0e0',
  flood: '#39c8e8',
  floodHot: '#d8fbff',
  /** Readability law: unblockable damage carries a red edge. The flood is not
   *  something you can block, shield or out-tank — so its rim is hot. */
  floodEdge: '#ff7a42',
  hook: '#5ce0ff',
} as const

// ---------------------------------------------------------------- water rules
// The zone's whole grammar in one block.
//
// FLAGGED (damageTickSeconds): the flood applies its damage in discrete ticks
// because the Warden carries TUNING.player.contactIframes (0.6s) of post-hit
// grace. A tick spacing *just above* that grace is what makes "15 dmg/s" mean
// 15 dmg/s instead of being silently halved by i-frames. If the grace moves in
// Phase 7 balance, this number moves with it.

export const ZONE2_WATER = {
  /** Multiplier on the Warden's move speed while standing on a 'W' tile. */
  slowMul: 0.7,
  /** …and while that water is actually surging. Wading a flood is worse. */
  floodSlowMul: 0.55,

  surge: {
    /** Seconds from the start of one surge cycle to the next (DESIGN: ~25s). */
    period: 25,
    /** Room seconds before the first telegraph: long enough to read the room. */
    firstDelay: 11,
    /** Klaxon-rumble + shimmer window (DESIGN: 3s). */
    telegraph: 3,
    /** Violent water (DESIGN: 4s). */
    duration: 4,
    /** DESIGN: 15 dmg/s to anything standing in flooded water. */
    damagePerSecond: 12,
    damageTickSeconds: 0.65,
    /** Current shove: impulse px/s, and how often it lands. */
    currentPush: 215,
    pushTickSeconds: 0.25,
    /** Screen shake while the flood runs. */
    shake: 3.2,
  },
} as const

/**
 * Which way each room's flood runs — the room's low side, chosen per template so
 * the current always shoves you somewhere the layout has an opinion about.
 * Normalised at use; a missing id falls back to due south.
 */
export const ZONE2_ROOM_CURRENT: Record<string, Vec> = {
  'z2-shallows': { x: -0.55, y: 0.84 },
  'z2-rootgallery': { x: 1, y: 0.12 },
  'z2-twinbasins': { x: 0.1, y: 1 },
  'z2-sluicecourt': { x: -1, y: 0.25 },
  'z2-idolflats': { x: 0.72, y: 0.7 },
  'z2-drownedworks': { x: -0.8, y: 0.6 },
  'z2-tidegallery': { x: 0.86, y: 0.5 },
}

// ---------------------------------------------------------------- enemies
// Three roles, three silhouettes (DESIGN §4): a low wide leaper, a squat armoured
// dome, a tall thin robe. None of them are slowed by the water — that asymmetry
// IS the zone (RESEARCH Q5 lever).

export const ZONE2_ENEMIES: EnemyDef[] = [
  {
    id: 'mireskipper',
    name: 'Mireskipper',
    role: 'swarm',
    hp: 15,
    speed: 104,
    radius: 9,
    touchDamage: 7,
    // Stock swarm brain, wrapped so the leap can throw a wake (see zone2.ts).
    ai: {
      custom: 'z2_mireskipper',
      lungeRange: 132,
      lungeCooldown: 1.7,
      windup: 0.34,
      lungeSpeed: 440,
      lungeTime: 0.3,
    },
    sprite: { key: 'mireskipper', size: 26, params: { skin: '#3f7a63', belly: '#bfe8c9', eye: '#ffe066' } },
    emberValue: 1,
    goldChance: 0.12,
  },
  {
    id: 'shellback',
    name: 'Shellback',
    role: 'tank',
    hp: 46,
    speed: 52,
    radius: 13,
    touchDamage: 9,
    ai: {
      custom: 'z2_shellback',
      /** Total width of the shell's blocking arc, in degrees. */
      frontalArcDeg: 130,
      /** How fast it can bring the shell round, rad/s. Circling beats it. */
      turnRate: 2.0,
      lungeRange: 112,
      lungeCooldown: 3.0,
      windup: 0.55,
      lungeSpeed: 470,
      lungeTime: 0.3,
      /** Multiplier on touchDamage for the snap itself. */
      snapDamage: 1.6,
      /** Seconds of exposed recovery after the snap — the flankless answer. */
      recover: 1.2,
    },
    sprite: { key: 'shellback', size: 34, params: { shell: '#4a5f52', rim: '#a7c9a0', flesh: '#8b6f5a' } },
    emberValue: 3,
    goldChance: 0.28,
  },
  {
    id: 'tidecaller',
    name: 'Tidecaller',
    role: 'support',
    hp: 26,
    speed: 66,
    radius: 11,
    touchDamage: 5,
    ai: {
      custom: 'z2_tidecaller',
      keepRange: 250,
      minRange: 200,
      hookRange: 430,
      hookCooldown: 5.0,
      /** Telegraphed channel before the hook lands (DESIGN: 1.5s). */
      channel: 1.5,
      /** Three discrete pulses — dash out between them and the hook breaks. */
      pulses: 3,
      pulseGap: 0.3,
      pullImpulse: 360,
      /** How far ahead of you it aims for deep water, px. */
      dragAhead: 128,
    },
    sprite: { key: 'tidecaller', size: 34, params: { robe: '#1f4a58', trim: '#5cf2cb', hook: '#9fe8ff' } },
    emberValue: 4,
    goldChance: 0.32,
  },
]

// ---------------------------------------------------------------- generators
// Three tiers (DESIGN §4): a fast cheap swarm source, a slow heavy tank source,
// a rare support source. Each keeps zone 1's violet "this is a source" rift so a
// generator is never mistaken for scenery, whatever body hangs on it.

export const ZONE2_GENERATORS: GeneratorDef[] = [
  {
    id: 'gen_rootcluster',
    name: 'Root-Cluster',
    hp: 60,
    radius: 15,
    spawns: 'mireskipper',
    interval: 3.1,
    burst: 2,
    cap: 6,
    sprite: { key: 'gen_rootcluster', size: 38, params: { root: '#2f8f74', glow: '#5cf2cb' } },
    emberValue: 4,
  },
  {
    id: 'gen_drownedwell',
    name: 'Drowned Well',
    hp: 98,
    radius: 16,
    spawns: 'shellback',
    interval: 6.2,
    burst: 1,
    cap: 3,
    sprite: { key: 'gen_drownedwell', size: 42, params: { stone: '#4c6a74' } },
    emberValue: 5,
  },
  {
    id: 'gen_tideidol',
    name: 'Tide-Idol',
    hp: 74,
    radius: 14,
    spawns: 'tidecaller',
    interval: 7.4,
    burst: 1,
    cap: 2,
    sprite: { key: 'gen_tideidol', size: 40, params: { stone: '#3d5f6b', eye: '#5ce0ff' } },
    emberValue: 6,
  },
]

// ---------------------------------------------------------------- rooms
// 44x26 at 32px, like zone 1, so the camera reads the same. Seven combat
// templates in three ordered difficulty groups — the runner (game/zones.ts)
// splits the combat library into roomCount contiguous groups and draws one from
// each, so a run always reads easy -> choice -> gate:
//
//   group 1 (1 generator):  z2-shallows, z2-rootgallery
//     water + Mireskippers. One causeway, one basin, one source.
//   group 2 (2 generators): z2-twinbasins, z2-sluicecourt
//     Shellbacks arrive, and the surge starts mattering: two sources on
//     opposite sides of water you have to be *in* to reach either.
//   group 3 (3 generators): z2-idolflats, z2-drownedworks, z2-tidegallery
//     all three bodies, the third source behind an iron gate ('L' + 'K' elite),
//     and the timed vault — 'V' across a low channel of 'W', so the vault is a
//     DOUBLE timing test: the zone clock says whether the door answers, and the
//     surge clock says whether the crossing kills you (DESIGN §5 Zone 2 Vault).
//     All three carry the vault so room 3 keeps its variety after the runner's
//     guaranteed-vault swap.
//
// Every combat template is 40-60% water by floor area, verified at authoring
// time and re-verified in test/zone2.mjs against the shipped strings.
//
// Glyphs: types.ts + D-011. 'W' shallow water, 'R' raised stone, '.' dry floor.

export const ZONE2_ROOMS: RoomTemplate[] = [
  {
    id: 'z2-shallows',
    generators: ['gen_rootcluster'],
    patrols: ['mireskipper'],
    map: [
      '############################################',
      '#..........................WWWWWWWWWWWWWWWW#',
      '#..........................WWWWWWWWWWWWWWWW#',
      '#...@....RR................WWWWWWWWWWWWWWWW#',
      '#........RR................RRRRRRRRRRRWWWWW#',
      '#........RR................RRRRRRRRRRRWWWWW#',
      '#WWWWWWWWRRWWWWWWWWWWWWWWWWRRRRRGRRR$RWWWWW#',
      '#WWWWWWWWRRWWWWWWWWWWWWWWWWRRRRRRRRRRRWWWWW#',
      '#WWWWWWWWRRWEWWWWWWWWWWWWWWRRRRRRRRRRRWWWWW#',
      '#WWWWWWWWRRWWWWW#####WWWRRRRRRRWRRWWWWWWWWW#',
      '#.......WRRWWWWW#####WWWRRRRRRRWRR.........#',
      '#.......WRRWWWWW#####WWWERRRRRRWRR.........#',
      '#.......WRR$WWWW#####WWWRRRRRRRWRR.........#',
      '#.......WRRWWWWW#####WWWWWWWWWWWRR.........#',
      '#..F....WRRWWWWWWWWWWWWWWWWWWWWWRR.........#',
      '#.......WRRWWWWWWWWWWWW####WWWWWRR.E.......#',
      '#.......WRRWWWWWWWWWWWW####WWWWWRR.........#',
      '#.......WRRWWWWWWWWWWWW####WWWWWRR.........#',
      '#.......WRRWWWWWWWWWWWWWWWWWWWWWRR.........#',
      '#WWWWWWWWRRRRRRRRRRRRRRRRRRRRRRRRRWWWWWWWWW#',
      '#WWWWWWWWRRR...........RRRRRRRRRRR.........#',
      '#WWWWWWWWWWW...........WWWWW...............#',
      '#WWWWWEWWWWW.....F.....WWWWW..F............#',
      '#WWWWWWWWWWW...........WWWWW...............#',
      '#WWWWWWWWWWW...........WWWWW...............#',
      '######################D#####################',
    ],
  },
  {
    id: 'z2-rootgallery',
    generators: ['gen_rootcluster'],
    patrols: ['mireskipper'],
    map: [
      '############################################',
      '#..........................................#',
      '#..@..........E................$...........#',
      '#..........C............................F..#',
      '#......................RR..................#',
      '#WWWWWWWWWWWWWWWWWWWWWWRRWWWWWWWWWWWWWWWWWW#',
      '#WWWWW####WWWWWWWWWW###RRWWWWWWWWW####WWWWW#',
      '#WWWWW####WWWWWWWWWW###RRWWWWWWWWW####WWWWW#',
      '#WWWWW####WWWWWWWWWW###RRWWWWWWWWW####WWWWW#',
      '#WWWWWWWWWWWWWWWWWWWWWWRRWWWWWWWWWWWWWRRRRW#',
      '#WWWRRRRRWWWWWWWWWWWWRRRRRRWWWWWWWWWWWRRRRW#',
      '#WWWRRRRRWWWRRRRRRWWWRRRRRRWWWRRRRRRWWRRRRW#',
      'DWWWRFRRRWWWRRR$RRWWWRRRERRWWWRRRRRRWWRGRRW#',
      '#WWWRRRRRWWWRRRRRRWWWRRRRRRWWWRRRRRRWWRRRRW#',
      '#WWWRRRRRWWWWWWWWWWWWRRRRRRWWWWWWWWWWWRRRRW#',
      '#WWWWWRRWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWRRRRW#',
      '#WWWWWRRWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWRRWWWWW####WWWWWWWWWW####WWWWWWWWWWWW#',
      '#WWWWWRRWWWWW####WWWWWWWWWW####WWWWWWWWWWWW#',
      '#WWWWWRRWWWWW####WWWWWWWWWW####WWWWWWWWWWWW#',
      '#WWWWWRRWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#.....RR...................................#',
      '#........E.......................E.........#',
      '#...................F......................#',
      '#..........................................#',
      '#####################D######################',
    ],
  },
  {
    id: 'z2-twinbasins',
    generators: ['gen_rootcluster', 'gen_drownedwell'],
    patrols: ['mireskipper', 'shellback'],
    map: [
      '############################################',
      '#..............WRRRRW###WWWWWWWWW..........#',
      '#..@...........WRRRRW###WWWWWWWWW..........#',
      '#.......E......WRRRRW###WWWWWWWWW.$........#',
      '#..............WRRRRW###WWWWWWWWW...#####..#',
      '#..............WRRRRW###WWWWWWWWW...#####..#',
      '#WWWWWWWWWWWRRRRRRRRW###WWRRRRRRW...#####..#',
      '#WWWWWWWWWWWRRRRWWWWW###WWRFRRRRWWWW#####WW#',
      '#WWWRRRRRRWWRRRRWWWWWRRRWWRRERRRWWWW#####WW#',
      '#WWWRRRRRRWWRGRRWWWWWRRRWWRRRRRRWWWWWWWWWWW#',
      '#WWWRRFRRRWWRRRRWWWWWRRRWWWWWWWWWWWWWWWWWWW#',
      '#WWWRRRRRRWWRRRRWWWWW###WWWWWWWWWWWWWWWWWWW#',
      '#......WWWWWRRRRWWWWW###WWWWWWWWWWWWWWWWWWWD',
      '#......WWWWWRRRRWWWWW###WWWWWWRRRRRRWWWWWWW#',
      '#......WWWWWWWWWWWWWW###WWWWWWRRRRRRWWWWWWW#',
      '#......WWWWWWWWWWWWWW###WWWWWWRRRGRRWWWWWWW#',
      '#......WWRRRRRRWWWWWWRRRWWWWWWRRRRRRWWWWWWW#',
      '#......WWRRRRRRWWWWWWRRRWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWRRRERRWWWWWWRRRRRRRRWWWWWWWWWEWWWW#',
      '#WWWWWWWWRRRRRRWWWWWW###RRRRRWWWWWWWWWWWWWW#',
      '#WW####WWWWWWWWW####W###RRERRW.............#',
      '#WW####WWWWWWWWW####W###RRRRRW.............#',
      '#WW####WWWW$WWWW####W###WRRRRR.............#',
      '#WW####WWWWWWWWW####W###WRRRRR.......F.....#',
      '#WWWWWWWWWWWWWWWWWWWW###WRRRRR.............#',
      '#################D##########################',
    ],
  },
  {
    id: 'z2-sluicecourt',
    generators: ['gen_drownedwell', 'gen_rootcluster'],
    patrols: ['shellback', 'mireskipper'],
    map: [
      '############################################',
      '#.........WWWWWWWWWWWWWWW.....P........WWWW#',
      '#..@......WWWWWWWWWWWWWWW..............WWWW#',
      '#.........WWWEWWWWWWWWWWW.$............GWWW#',
      '#....####.WWWWWWWWWWWWWWW..........####WWWW#',
      '#WWWW####..........WWWWWWWWWWWWWW..####WWWW#',
      '#WWWW####.......F..WWWWWWWWWWWWWW..####WWWW#',
      '#WWWW####..........WWWWWWWWWWWWWW..####....#',
      '#WWWW..............WWWWWWWWWWWWWW..........#',
      '#WWWW..............WWRRRWWWWWWWWW........F.#',
      '#WWWWWWWWWWWWWWWWWWWWRRRWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWRRRRRRRRRRRWWWWWWWWWWWWWWW#',
      '#WEWWWWWWWWWRRRWWRRRRRRRRRRRWWRRRWWWWWWWWWWD',
      '#WWWWWWWWWWWRRRWWRRRRRERRRRRWWRRRWWWWWWWWWW#',
      '#WWWWWWWWWWWRRRWWRRRRRRRRRRRWWRRRWWWWWWWWWW#',
      '#WWWWWWWWWWWWWWWWRRRRRRRRRRRWWWWWWWWWWWWEWW#',
      '#WWWWWWWWWWWWWWWWWWWWRRRWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWWWWWW........WWRRRWWWWWWWWWWW........#',
      '#WWWWWWWWWW........WWWWWWWWWWWWWWWW........#',
      '#WWWWWWWWWW........WWWWWWWWWWWWWWWW####....#',
      '#WWWWWWWWWW...WWWWWWWWWWW......E...####....#',
      '#....####.G...WWWWWWWWWWW...F......#WWWWWWW#',
      '#....####.....W$WWWWWWWWW..........#WWWWWWW#',
      '#.............WWWWWWWWWWW...........WWWWWWW#',
      '#.............WWWWWWWWWWW...........WWWWWWW#',
      '######################D#####################',
    ],
  },
  {
    id: 'z2-idolflats',
    generators: ['gen_rootcluster', 'gen_drownedwell', 'gen_tideidol'],
    patrols: ['mireskipper', 'shellback', 'tidecaller'],
    map: [
      '############################################',
      '#............WWWWWWWWWWWWWWWWWWWW###########',
      '#............WWWWWWWWWWWWWWWWWWWW##RRRRRRR##',
      '#..@.........WWWWWWWRRRRRRRRRRRWW##RRRRRRR##',
      '#............WWWWWWWRRKRRRRRRRRRRRLRRRGRRR##',
      '#...F........WWWWWWWRRRRRRRFRRRRRRLRRRRRRR##',
      '#.....RR.....WWWWWWWRRRRRRRRRRRRRRLRRRRRCR##',
      '#WWWWWRRWWWWWWWWWWWWWWWWWRRWWWWWW##RRRRRRR##',
      '#WWWWWRRWWWWEWWWWWWWWWWWWRRWWWWWW###########',
      '#WWWWWRRWWWWWWWWWWWWWWWWWRRWWWWWWWWWWWWWWWW#',
      '#WWWWWRRWWWWWWWWWWWWWWWWWRRWWWWWWWWWWEWWWWW#',
      '#WWWWWRRWWWWWWWWWWWWWWWWWRRWWWWWWWWWWWWWWWW#',
      '#WWWWWRRRRGRRRRRRRRRRRERRRRRRRRRRRRRRRRWWWW#',
      '#WWWWWRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRRWWWW#',
      '#WWWWWRRWWWWWWWRRWWWWWWWWWWWWWWWWRRGRRRWWWW#',
      '#WWWWWRRWWWWWWWRRWWWWWWWWWWWWWWWWRRRRRRWWWW#',
      '#WWWWWRRWWWWWWWRRRRRRWWWWWWWWWWWWRRRRRRWWWW#',
      '#WWWWWRRWWWWWWWRRWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '#WWWWWRRWWWWWWWRRWWWWWWWWWWWWW.............#',
      '#.....RR.......RRWWWWWWWWWWWWW.............#',
      '#.....RR.......WW###V#V###WWWWE............#',
      '#.......E..$...W#vvvvvvvvv#WWW....F........D',
      '#..............W#vvvvvvvvv#WWW.............#',
      '#..............W#vvvvvvvvv#WWW.............#',
      '#..............W#vvvvvvvvv#WWW.............#',
      '##D#########################################',
    ],
  },
  {
    id: 'z2-drownedworks',
    generators: ['gen_rootcluster', 'gen_tideidol', 'gen_drownedwell'],
    patrols: ['mireskipper', 'shellback', 'tidecaller'],
    map: [
      '############################################',
      '#..........................................#',
      '#....F..............E................#vvvvv#',
      '#................................WWWW#v$vvv#',
      '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWVvvvvv#',
      '#...WWWWWWWWWWWWWRRRRRRRRRFRRRRWWWWWW#vvvvv#',
      '#.@.WWWWWRRRWWWWWRRRRRRRRRRRRRRWWWWWW#vvvvv#',
      '#...WWWWWRRRWWWWWRRWWWWWWWWWWRRWWWWWWW######',
      '#..RRRRRRRRRW###WRRWWWWWWWWWWRREWWWWWWWWWWW#',
      '#..RRRRRRRRRW###WRRWWWWWWWWWWRRWWWRRRRRRRWW#',
      '#...WWWWWRGRW###WRRWWWWWWWWWWRRWWWRRRRRRRWW#',
      '#...WWWWWRRRW###WRRWWWWWWWWWWRRRRRRRRRRRRWW#',
      '#...WWWWWRRRW###WREWWWWWWWWWWRRRRRRRRGRRRWW#',
      '#...WWWWWRRRWWWWWRRWWWWWWWWWWRRWWWRRRRRRRWW#',
      '#...WWWWWRRRWWWWWRRWWWWWWWWWWRRWWWRRRRRRRWW#',
      '#...WWWWWRRRRRRRRRRRRRR$RWWWWWWWWWWWWWWWWWW#',
      '#...WWWWWRRRRRRRRRRRRRRRRRWWWWWWWWWWWWWWWWW#',
      '#...WWWWWRRRWWWWWWWWWWWWRRWWWWWWWWWWWWWWWWWD',
      '#...#LLL#RRRWWWEWWWWWWWWRRRRRRWWWWWWWWEWWWW#',
      '#...#RRR#RRRWWWWWWWWWWWWRRRRRRWWW####WWWWWW#',
      '#...#RRR#RRRWWWWWWWW###WRRRKRRWWW####WWWWWW#',
      '#...#RGR#RRRFWWWWWWW###WRRRRRRWWW####WWWWWW#',
      '#...#RRR#RRRWWWWWWWW###WRRRRRRWWW####WWWWWW#',
      '#...#RRR#WWWWWWWWWWW###WWWWWWWWWWWWWWWWWWWW#',
      '#...#####WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
      '###############D############################',
    ],
  },
  {
    id: 'z2-tidegallery',
    generators: ['gen_rootcluster', 'gen_drownedwell', 'gen_tideidol'],
    patrols: ['mireskipper', 'tidecaller', 'shellback'],
    map: [
      '############################################',
      '#.........WWWWWWWWWWWWWWWWWWWWWWWW.........#',
      '#.........WWWWWWWWWWWWWWWWWWWWWWWW......F..#',
      '#..@......WWEWWWWWWRRRRRRRRRRRRRRRR...E....#',
      '#.F.......WWWWWWWWWRRRRRRRRRRRRRRRR........#',
      '#....RR...WWWWWWWWWRRWWWWWWWWW#######......#',
      '#WWWWRRWWWWWWWWWWWWRRWWWWWWWWWLRRGRR#WWWWWW#',
      '#WWWWRRWWWWWWWWWWWWRRWW####WWWLRRRRR#WWWWWW#',
      '#WWWWRRWWWWWWWWWWWWRRWW####WWW#######WWWWWW#',
      '#WWWWRRRRRRRCRRRRRRRRWW####WWWWWRRRRRRRRRRW#',
      '#WWWWRRRRRRRRRRRRRRRRWW####WWWWWRRRRRRRRRRW#',
      '#WWWWRRWWWWWWWWWWWWWWWW####WWWWWWWWWWRRRRRW#',
      '#WWWWRRWW####WWWWWWWWEWWWWWWWWWWWWWWWRRRRRWD',
      '#WWWWRRWW####WWWWWWWWWWWWWWWWWWWWWWWWRRRRRW#',
      '#WWWWRRWW####WWRRRRRRRKRRRRRRWWWWWWWWRRGRRW#',
      '#WWWWRRWWWWWWWRRRRRRRRRRRRRRRWWW####WRRRRRW#',
      '#WWWWRRWWWWWWWRRWWWWWWWWWWWRRWWW####WRRRRRW#',
      '#WWWWRRWWWRRRRRRWWWWWWWWWWWRRWWW####WRRRRRW#',
      '#WWWWRRWGWRRRRRRWWWWWWWWWWWRRWWW####WRRRRRW#',
      '#WWWWRRWWWRRRRRRWWWWWWWWWWWRRWWWWWWWWRRR$RW#',
      '#....RR...RRRRRR###V#V###WWRRRFRRRRRRRRRRRW#',
      '#...$RR......WW#vvvvvvvvv#WRRRRRRRRRRRRRRRW#',
      '#......E.....WW#vvvvvvvvv#WRRWWWWWWWEWWWWWW#',
      '#............WW#vvvvvvvvv#WWWWWWWWWWWWWWWWW#',
      '#............WW#vvvvvvvvv#WWWWWWWWWWWWWWWWW#',
      '#########D##################################',
    ],
  },
  {
    id: 'z2-respite',
    generators: [],
    patrols: [],
    map: [
      '#################S################',
      '#................................#',
      '#............RRRRRRRR............#',
      '#..WWWWWW....RRRRRRRR....WWWWWW..#',
      '#..WWWWWW................WWWWWW..#',
      '#..WWWWWW...RR......RR...WWWWWW..#',
      '#..WWWWWW...RR......RR...WWWWWW..#',
      '#..WWWWWW...RR......RR...WWWWWW..#',
      '#.........A.RR......RR.N.........#',
      '#...........RR......RR...........#',
      '#...........RR......RR...........#',
      '#..WWWWWW...RR......RR...WWWWWW..#',
      '#..WWWWWW...RR......RR...WWWWWW..#',
      '#..WWWWWW.......@........WWWWWW..#',
      '#..WWWWWW................WWWWWW..#',
      '#..WWWWWW................WWWWWW..#',
      '#................................#',
      '##################################',
    ],
  },
]

// ============================================================================
// BOSS 2 — THE CISTERN KING (DESIGN §5 Zone 2)
// "Verb: terrain control — the arena itself is the attack." Every number the
// fight reads lives here; content/bosses/boss2.ts holds no literals.
// ============================================================================

// ---------------------------------------------------------------- boss arena
// 44x21 (the same 21 rows zone 1's arena settled on after the framing pass, so
// the whole fight fits the camera). Four stone quadrant-platforms stand in a
// black moat; the throne dais is upstage centre. There is no cover and there are
// no destructible parts — the only question in this room is where the floor is.
export const ZONE2_BOSS_ARENA: RoomTemplate = {
  id: 'z2-cisternking',
  generators: [],
  patrols: [],
  map: [
    '############################################',
    '#WWWWWWWWWWWWWWWWWRRRRRRRRWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWRRRRRRRRWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWRRRRRRRRWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWRRRRRRRRWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWRRRRR@RRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWRRRRRRRRRRRWWWWWWWRRRRRRRRRRRWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#WWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWWW#',
    '#####################D######################',
  ],
}

/**
 * Boss tuning block. Arrays indexed by phase-1 (P1, P2, P3).
 *
 * FLAGGED INTERPRETATION (`glob`): DESIGN calls the brine-globs "lobbed AoE
 * projectiles, green". They are hand-rolled inside the boss rather than pushed
 * through the shared projectile pool, because RunCtx.spawnProjectile cannot aim
 * a lobbed shot — it hard-codes the impact point to 220px along the fired
 * direction (game/game.ts spawnProjectileEx). Hand-rolling buys a real landing
 * marker, which is the clearest possible telegraph for a falling AoE. The cost:
 * an Emberburst does not delete globs in flight the way it deletes ordinary
 * enemy projectiles. Noted for the orchestrator; the fix is a shared aimed-lob
 * seam on RunCtx, which is not a zone worker's to add.
 *
 * FLAGGED INTERPRETATION (no destructible parts): DESIGN §5 says the drains
 * "pour Mireskippers" in P2 and that this fight is "distinct from Broodgate:
 * zero add-priority puzzle, pure terrain rhythm". Making the drains BossParts
 * would hand the player exactly the add-priority puzzle the design forbids, so
 * they are scenery with a timer. Snapshot.generators is 0 for this fight, on
 * purpose.
 */
export const ZONE2_BOSS = {
  id: 'cisternking',
  name: 'THE CISTERN KING',

  /**
   * World-pixel seat of the throne, dead centre of the dais block (tiles 18-25
   * on row 1-4 of ZONE2_BOSS_ARENA, so the centre lands on a tile boundary and
   * has to be written in pixels rather than tiles).
   */
  thronePos: { x: 22 * 32, y: 4 * 32 + 6 },
  hp: 850,
  radius: 40,

  /** DESIGN §5 Boss 2: phases at 66% and 33%. */
  phase2At: 0.66,
  phase3At: 0.33,

  /**
   * The four quadrant platforms, as tile rects matching the arena's 'R' blocks.
   * Order is NW, NE, SW, SE; `oppositePairs` indexes into this list.
   */
  quadrants: [
    { name: 'NORTH-WEST', x0: 8, y0: 6, x1: 18, y1: 10 },
    { name: 'NORTH-EAST', x0: 26, y0: 6, x1: 36, y1: 10 },
    { name: 'SOUTH-WEST', x0: 8, y0: 13, x1: 18, y1: 17 },
    { name: 'SOUTH-EAST', x0: 26, y0: 13, x1: 36, y1: 17 },
  ],
  /** P2 floods two *opposite* quadrants at once. */
  oppositePairs: [
    [0, 3],
    [1, 2],
  ] as ReadonlyArray<readonly [number, number]>,
  /** P1 rotation order — a learnable beat, not a coin flip. */
  rotation: [0, 1, 3, 2],

  flood: {
    /** Telegraph, then flood, then a breath. Seconds, per phase. */
    telegraph: [1.9, 1.7, 1.5],
    duration: [3.6, 3.4, 3.2],
    gap: [2.8, 2.3, 1.8],
    damagePerSecond: 12,
    damageTickSeconds: 0.65,
    push: 205,
    pushTickSeconds: 0.3,
  },

  /** Brine-globs: green, lobbed, land on a marked circle. */
  glob: {
    interval: [3.4, 2.9, 2.6],
    count: [2, 3, 3],
    windup: 0.55,
    flightSeconds: 1.15,
    /** How far the landing marker is scattered around the Warden, px. */
    scatter: 74,
    splash: 48,
    damage: 14,
    arcHeight: 130,
    color: '#8fe36a',
  },

  /** The slam wave: a ring across the water. Dry stone is safe; so is a dash. */
  wave: {
    /** 0 = the phase does not use it. */
    interval: [0, 6.4, 4.4],
    windup: 0.9,
    speed: 320,
    thickness: 34,
    damage: 16,
    maxRadius: 820,
  },

  /** P2's two drains. Scenery, on a timer — see the flag above. */
  pour: {
    interval: [0, 5.2, 4.4],
    count: [0, 2, 3],
    liveCap: 14,
    drainTiles: [
      { tx: 3, ty: 10 },
      { tx: 40, ty: 10 },
    ],
    body: 'mireskipper',
  },

  /** P3: he leaves the throne and drowns the ground he stands on. */
  wade: {
    speed: 46,
    /** Seconds standing in a quadrant before it is his for good. */
    claimSeconds: 2.6,
    /** Never take the last two: safe ground shrinks to ~2 platforms, not zero. */
    permanentCap: 2,
  },

  death: { shake: 14, shakeSeconds: 1.2, hitstop: 0.32 },
} as const

// ---------------------------------------------------------------- zone

export const ZONE2: ZoneConfig = {
  id: 2,
  name: 'The Drowned Roots',
  tagline: 'The floor is a rhythm. Learn the tide, or drown standing.',
  palette: ZONE2_PALETTE,
  rooms: ZONE2_ROOMS,
  roomCount: 3,
  // DESIGN §5 Zone 2: standard fill. The flood is the zone's own clock and pays
  // nothing into this one — the two timers are deliberately independent.
  gloomFillSeconds: 95,
  vaultClockSeconds: 150,
  eliteRate: 0.1,
  eliteRiders: ['frenzied', 'bloated', 'shielded'],
  statScale: { hp: 1.12, damage: 1.1 },
  // DESIGN §11: the healing-scarcity curve. Zone 1 gave 2-3 a room; here it is
  // 1-2, and zone 5 will sell it to you.
  foodPerRoom: [1, 2],
  music: {
    root: 41,
    mode: 'phrygian',
    tempo: 76,
    padLevel: 1,
    bassLevel: 0.72,
    percLevel: 0.6,
    character: 'drips',
  },
  bossId: 'cisternking',
}
